import { Link } from "@builtbyted/homeframe";
import { Bars, LineChart, type Series } from "./Charts";
import {
  Page,
  Section,
  Skeleton,
  Message,
  Poster,
  DayPicker,
  Metric,
  scoped,
  useData,
  useQuery,
  count,
  duration,
  date,
  str,
  num,
  type Row,
} from "./ui";

interface MediaData {
  title: string;
  media_type: string;
  year: number;
  server_id: number;
  thumb: string;
  summary: string;
  plays: number;
  duration: number;
  users: number;
  grain: string;
  daily: Row[];
  viewers: Row[];
  platforms: Row[];
  recent: Row[];
}
export function Media() {
  const url = useQuery();
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const data = useData<MediaData>(
    scoped("/media", url, {
      title: url.searchParams.get("title") || "",
      media_type: url.searchParams.get("type") || "movie",
      year: url.searchParams.get("year") || "0",
      days: url.searchParams.get("days") || "30",
      timezone,
    }),
  );
  const d = data.data;
  const series: Series[] = d
    ? [
        {
          label: "Plays",
          points: d.daily.map((day) => [
            Date.parse(
              str(day.day) + (d.grain === "month" ? "-01" : "") + "T12:00:00",
            ) / 1000,
            num(day.plays),
          ]),
        },
      ]
    : [];
  return (
    <Page
      title={d?.title || url.searchParams.get("title") || "Title details"}
      tools={<DayPicker />}
    >
      {data.error && <Message error>{data.error}</Message>}
      {!d && !data.error && <Skeleton kind="page" />}
      {d && (
        <>
          <div className="media-overview">
            <Poster server={d.server_id} path={d.thumb} />
            <div>
              <span className="badge">
                {d.media_type === "movie" ? "Movie" : "TV series"}
                {d.year ? ` · ${d.year}` : ""}
              </span>
              {d.summary && <p>{d.summary}</p>}
              <div className="metrics">
                <Metric label="Plays" value={count(d.plays)} />
                <Metric label="Watch time" value={duration(d.duration)} />
                <Metric label="Viewers" value={count(d.users)} />
              </div>
            </div>
          </div>
          <div className="dashboard-grid">
            <section className="viz-panel span-12">
              <div className="viz-heading">
                <h2>Viewing over time</h2>
              </div>
              <LineChart label={`${d.title} viewing history`} series={series} />
            </section>
            <section className="viz-panel span-6">
              <div className="viz-heading">
                <h2>Top viewers</h2>
              </div>
              <Bars rows={d.viewers} />
            </section>
            <section className="viz-panel span-6">
              <div className="viz-heading">
                <h2>Devices</h2>
              </div>
              <Bars rows={d.platforms} />
            </section>
          </div>
          <Section title="Who watched">
            <div className="table-wrap">
              <table className="media-viewers-table">
                <thead>
                  <tr>
                    <th>Viewer</th>
                    <th>Plays</th>
                    <th>Watch time</th>
                    <th>Last watched</th>
                  </tr>
                </thead>
                <tbody>
                  {d.viewers.map((viewer) => (
                    <tr key={str(viewer.label)}>
                      <td>
                        <strong>{str(viewer.label)}</strong>
                      </td>
                      <td>{count(viewer.plays)}</td>
                      <td>{duration(viewer.duration)}</td>
                      <td>{date(viewer.last_seen)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {!d.viewers.length && (
                <div className="empty-note">No plays in this period.</div>
              )}
            </div>
          </Section>
          <Section title="Recent plays">
            <div className="media-plays">
              {d.recent.map((play) => (
                <Link
                  className="media-play"
                  key={str(play.id)}
                  to={`/history/${play.id}${url.search}`}
                >
                  <div>
                    <strong>{str(play.username)}</strong>
                    <span>
                      {d.media_type === "show"
                        ? str(play.title)
                        : str(play.platform)}
                    </span>
                    <small>{str(play.server_name)}</small>
                  </div>
                  <div>
                    <time>{date(play.started)}</time>
                    <small>{duration(play.duration)}</small>
                  </div>
                  <span aria-hidden="true">›</span>
                </Link>
              ))}
            </div>
          </Section>
        </>
      )}
    </Page>
  );
}
